import { CHAT_POLL_IDLE_MS, useChatMessages } from '@/api/hooks/useChatMessages'
import { useFetchBillingRecord } from '@/api/hooks/useFetchBillingRecord'
import { useFetchLatestAppoinmentResults } from '@/api/hooks/useFetchLatestAppointmentResults'
import { useFetchPatientInfo } from '@/api/hooks/useFetchPatientInfo'
import { useFetchPatientReferral } from '@/api/hooks/useFetchPatientReferral'
import { useFetchPendingForms } from '@/api/hooks/useFetchPendingForms'
import { useFetchUpcomingVisits } from '@/api/hooks/useFetchUpcomingVisits'
import { usePaymentPlans } from '@/api/hooks/usePaymentPlans'
import { useFocusEffect } from 'expo-router'
import { isEmpty } from 'ramda'
import { useCallback, useMemo, useState } from 'react'
import { buildHomeTiles } from './homeTiles'
import { useResultsSeen } from './resultsSeen'

/** The clock the greeting reads, refreshed whenever the screen comes back into view. */
const useNow = () => {
	const [now, setNow] = useState(() => new Date())
	useFocusEffect(
		useCallback(() => {
			setNow(new Date())
		}, [])
	)
	return now
}

/**
 * Every read the home screen depends on, folded into the tile list.
 *
 * The older API methods swallow failures into `{}` or `[]`; those sentinels become `null`
 * here so the engine can tell a failed read from an empty account. The two new methods throw.
 */
export const useHomeTiles = () => {
	const now = useNow()
	const { patientInfo } = useFetchPatientInfo()
	const visitsQuery = useFetchUpcomingVisits()
	const formsQuery = useFetchPendingForms()
	const resultsQuery = useFetchLatestAppoinmentResults()
	const billingQuery = useFetchBillingRecord()
	const plansQuery = usePaymentPlans()
	const referralQuery = useFetchPatientReferral()
	const seenResultsAppointmentId = useResultsSeen()
	const chat = useChatMessages(CHAT_POLL_IDLE_MS)
	// No count at all when the chat is not reachable: the tile then just says we are here.
	const supportUnread = chat.isError || chat.isLoading ? undefined : chat.unreadCount

	const tiles = useMemo(
		() =>
			buildHomeTiles({
				now,
				firstName: patientInfo.firstName,
				visits: visitsQuery.isLoading
					? undefined
					: visitsQuery.isError
					  ? null
					  : visitsQuery.visits ?? null,
				pendingForms: formsQuery.isLoading ? undefined : formsQuery.pendingForms,
				results: resultsQuery.isLoadingResults
					? undefined
					: resultsQuery.isUnavailable
					  ? null
					  : {
								appointmentId: resultsQuery.appointmentId,
								healthScore: resultsQuery.appointmentResults?.healthScore,
						  },
				seenResultsAppointmentId,
				billing: billingQuery.isLoading
					? undefined
					: isEmpty(billingQuery.billingRecord)
					  ? null
					  : billingQuery.billingRecord,
				plans: plansQuery.isLoading
					? undefined
					: plansQuery.isError
					  ? null
					  : plansQuery.plans ?? null,
				referral: referralQuery.isLoading
					? undefined
					: referralQuery.hasRenderableReferral &&
						  referralQuery.discountInCents !== undefined
					  ? { discountInCents: referralQuery.discountInCents }
					  : null,
				supportUnread,
			}),
		[
			supportUnread,
			now,
			patientInfo.firstName,
			visitsQuery.isLoading,
			visitsQuery.isError,
			visitsQuery.visits,
			formsQuery.isLoading,
			formsQuery.pendingForms,
			resultsQuery.isLoadingResults,
			resultsQuery.isUnavailable,
			resultsQuery.appointmentId,
			resultsQuery.appointmentResults?.healthScore,
			seenResultsAppointmentId,
			billingQuery.isLoading,
			billingQuery.billingRecord,
			plansQuery.isLoading,
			plansQuery.isError,
			plansQuery.plans,
			referralQuery.isLoading,
			referralQuery.hasRenderableReferral,
			referralQuery.discountInCents,
		]
	)

	return { tiles, refetchVisits: visitsQuery.refetch }
}
